/**
 * Component: EventForm
 * Form for creating and editing events
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileInput } from '@/components/ui/file-input';
import { PriceInput } from '@/components/ui/price-input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { EventFormData, EventFormErrors } from '../types';
import type { EventCodeStatus } from '../hooks/useEventForm';
import { registrationUrl } from '../public/publicUrls';

interface EventFormProps {
  formData: EventFormData;
  errors: EventFormErrors;
  touched: Partial<Record<keyof EventFormData, boolean>>;
  isSubmitting: boolean;
  isEditMode: boolean;
  hasChanges: boolean;
  onFieldChange: <K extends keyof EventFormData>(
    field: K,
    value: EventFormData[K],
  ) => void;
  onFieldBlur: (field: keyof EventFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  flyerPreview: string | null;
  onFlyerSelect: (file: File | null) => void;
  paymentQrPreview: string | null;
  onPaymentQrSelect: (file: File) => void;
  onPaymentQrRemove: () => void;
  codeStatus: EventCodeStatus;
  /** Re-apply the name-based code suggestion (new events) */
  onSuggestCode: () => void;
  /** Editing a published event's code invalidates links already shared */
  codeChangeWarning: boolean;
}

export function EventForm({
  formData,
  errors,
  touched,
  isSubmitting,
  isEditMode,
  hasChanges,
  onFieldChange,
  onFieldBlur,
  onSubmit,
  onCancel,
  flyerPreview,
  onFlyerSelect,
  paymentQrPreview,
  onPaymentQrSelect,
  onPaymentQrRemove,
  codeStatus,
  onSuggestCode,
  codeChangeWarning,
}: EventFormProps) {
  const [qrError, setQrError] = useState<string | null>(null);

  // New event: start typing the name right away (the code follows it)
  const nameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isEditMode) nameInputRef.current?.focus();
  }, [isEditMode]);

  const handleQrChange = (file: File | null) => {
    if (file && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setQrError('Formato no válido. Use JPG, PNG o WebP.');
      return;
    }
    if (file && file.size > 5 * 1024 * 1024) {
      setQrError('La imagen supera los 5 MB.');
      return;
    }
    setQrError(null);
    if (file) onPaymentQrSelect(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {isEditMode ? 'Editar Evento' : 'Crear Nuevo Evento'}
          </CardTitle>
          <CardDescription>
            {isEditMode
              ? 'Modifica los datos del evento'
              : 'Completa la información para crear un nuevo evento'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">
              Código <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              type="text"
              placeholder="CONV26"
              value={formData.code}
              onChange={(e) => onFieldChange('code', e.target.value)}
              onBlur={() => onFieldBlur('code')}
              disabled={false}
              maxLength={20}
              autoComplete="off"
              className={`font-mono uppercase ${
                (touched.code && errors.code) || codeStatus === 'taken' ? 'border-destructive' : ''
              }`}
            />
            {touched.code && errors.code && (
              <p className="text-sm text-destructive">{errors.code}</p>
            )}
            {!errors.code && codeStatus === 'taken' && (
              <p className="text-sm text-destructive">Ya existe un evento con este código</p>
            )}
            {formData.code && !errors.code && codeStatus !== 'taken' && (
              <p className="break-all text-sm text-muted-foreground">
                Enlace público: <span className="font-mono">{registrationUrl(formData.code)}</span>
                {codeStatus === 'checking' && ' · verificando…'}
                {codeStatus === 'ok' && ' · disponible'}
              </p>
            )}
            {!isEditMode && (
              <p className="text-sm text-muted-foreground">
                Se sugiere a partir del nombre; puede editarlo (3–20 caracteres: letras, números y guiones).{' '}
                <button type="button" className="underline underline-offset-2" onClick={onSuggestCode}>
                  Sugerir desde el nombre
                </button>
              </p>
            )}
            {codeChangeWarning && (
              <p className="text-sm text-amber-700">
                Cambiar el código invalida los enlaces de inscripción ya compartidos. Las credenciales y QR existentes
                no se ven afectados.
              </p>
            )}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre del Evento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              ref={nameInputRef}
              type="text"
              placeholder="Ej: Convención Nacional 2026"
              value={formData.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              onBlur={() => onFieldBlur('name')}
              className={
                touched.name && errors.name ? 'border-destructive' : ''
              }
            />
            {touched.name && errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              placeholder="Descripción detallada del evento..."
              rows={4}
              value={formData.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
              onBlur={() => onFieldBlur('description')}
            />
            <p className="text-sm text-muted-foreground">Opcional</p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">
              Ubicación <span className="text-destructive">*</span>
            </Label>
            <Input
              id="location"
              type="text"
              placeholder="Ej: La Paz, Bolivia"
              value={formData.location}
              onChange={(e) => onFieldChange('location', e.target.value)}
              onBlur={() => onFieldBlur('location')}
              className={
                touched.location && errors.location ? 'border-destructive' : ''
              }
            />
            {touched.location && errors.location && (
              <p className="text-sm text-destructive">{errors.location}</p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="start_date">
                Fecha de Inicio <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="start_date"
                  type="date"
                  value={
                    formData.start_date
                      ? formData.start_date.toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    onFieldChange(
                      'start_date',
                      e.target.value ? new Date(e.target.value) : null,
                    )
                  }
                  onBlur={() => onFieldBlur('start_date')}
                  className={
                    touched.start_date && errors.start_date
                      ? 'border-destructive'
                      : ''
                  }
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              </div>
              {touched.start_date && errors.start_date && (
                <p className="text-sm text-destructive">{errors.start_date}</p>
              )}
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="end_date">
                Fecha de Finalización{' '}
                <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="end_date"
                  type="date"
                  value={
                    formData.end_date
                      ? formData.end_date.toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    onFieldChange(
                      'end_date',
                      e.target.value ? new Date(e.target.value) : null,
                    )
                  }
                  onBlur={() => onFieldBlur('end_date')}
                  className={
                    touched.end_date && errors.end_date
                      ? 'border-destructive'
                      : ''
                  }
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              </div>
              {touched.end_date && errors.end_date && (
                <p className="text-sm text-destructive">{errors.end_date}</p>
              )}
            </div>
          </div>

          {/* Base Price */}
          <div className="space-y-2">
            <Label htmlFor="base_price">
              Precio base (Bs.) <span className="text-destructive">*</span>
            </Label>
            <PriceInput
              id="base_price"
              value={formData.base_price}
              onValueChange={(v) => onFieldChange('base_price', v)}
              onBlur={() => onFieldBlur('base_price')}
              className={
                touched.base_price && errors.base_price
                  ? 'border-destructive'
                  : ''
              }
            />
            {touched.base_price && errors.base_price && (
              <p className="text-sm text-destructive">{errors.base_price}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Este será el precio por defecto antes de aplicar reglas de
              descuento
            </p>
          </div>

          {/* Agenda */}
          <div className="space-y-2">
            <Label htmlFor="agenda">Orden del día</Label>
            <Textarea
              id="agenda"
              placeholder="Describe el programa del evento, actividades y horarios..."
              rows={6}
              value={formData.agenda || ''}
              onChange={(e) => onFieldChange('agenda', e.target.value)}
              onBlur={() => onFieldBlur('agenda')}
            />
            <p className="text-sm text-muted-foreground">Opcional</p>
          </div>

          {/* Flyer Upload */}
          <div className="space-y-2">
            <Label htmlFor="flyer">Afiche del evento</Label>
            <FileInput
              id="flyer"
              accept="image/jpeg,image/png,image/webp"
              onChange={(file) => file && onFlyerSelect(file)}
              fileName={formData.flyer?.name ?? (flyerPreview ? 'Afiche actual' : null)}
              invalid={!!(touched.flyer && errors.flyer)}
            />
            {touched.flyer && errors.flyer && (
              <p className="text-sm text-destructive">{errors.flyer}</p>
            )}
            <p className="text-sm text-muted-foreground">
              {isEditMode && flyerPreview
                ? 'Selecciona un nuevo archivo para reemplazar el afiche actual'
                : 'Formatos aceptados: JPG, PNG, WebP (máx. 10 MB)'}
            </p>

            {/* Flyer Preview */}
            {flyerPreview && (
              <div className="mt-4">
                <Label>Vista Previa:</Label>
                <div className="mt-2 rounded-lg border bg-muted/50 p-4">
                  <img
                    src={flyerPreview}
                    alt="Vista previa del afiche"
                    className="mx-auto h-auto max-h-64 max-w-full rounded"
                  />
                </div>
              </div>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{errors.general}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Public pre-registration settings */}
      <Card>
        <CardHeader>
          <CardTitle>Inscripción pública</CardTitle>
          <CardDescription>
            Formulario de preinscripción sin cuenta, disponible mientras el evento esté abierto
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo de formulario</Label>
              <Select
                value={formData.registration_form_type}
                onValueChange={(v) =>
                  onFieldChange('registration_form_type', v as EventFormData['registration_form_type'])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SIMPLE">Simple (nombre, CI, celular, distrito)</SelectItem>
                  <SelectItem value="FULL_CONVENTION">Completo (Formulario de Registro oficial)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Firma del formulario</Label>
              <Select
                value={formData.signature_mode}
                onValueChange={(v) => onFieldChange('signature_mode', v as EventFormData['signature_mode'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ON_SITE">Presencial (firma en el formulario impreso)</SelectItem>
                  <SelectItem value="DIGITAL">Digital (firma obligatoria en pantalla al inscribirse)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_deadline">Fecha límite de inscripción</Label>
              <Input
                id="registration_deadline"
                type="datetime-local"
                value={formData.registration_deadline}
                onChange={(e) => onFieldChange('registration_deadline', e.target.value)}
              />
              <p className="text-sm text-muted-foreground">Opcional. Vacío = hasta cerrar inscripciones</p>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <div>
                <Label htmlFor="allow_public_guests">Permitir invitados</Label>
                <p className="text-sm text-muted-foreground">Personas que no son ministros</p>
              </div>
              <Switch
                id="allow_public_guests"
                checked={formData.allow_public_guests}
                onCheckedChange={(c) => onFieldChange('allow_public_guests', c)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_instructions">Instrucciones de pago</Label>
            <Textarea
              id="payment_instructions"
              rows={3}
              placeholder="Banco, Nº de cuenta, titular..."
              value={formData.payment_instructions}
              onChange={(e) => onFieldChange('payment_instructions', e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Se muestra antes de subir el comprobante de pago
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_qr">QR de pago del banco</Label>
            <FileInput
              id="payment_qr"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleQrChange}
              fileName={formData.payment_qr?.name ?? (paymentQrPreview ? 'QR actual' : null)}
              invalid={!!qrError}
            />
            {qrError && <p className="text-sm text-destructive">{qrError}</p>}
            <p className="text-sm text-muted-foreground">
              Imagen del QR de la cuenta (JPG, PNG o WebP, máx. 5 MB). Quien se inscriba podrá escanearlo o descargarlo
              con el botón «Guardar QR».
            </p>
            {paymentQrPreview && (
              <div className="flex items-start gap-3 rounded-lg border bg-muted/50 p-3">
                <img
                  src={paymentQrPreview}
                  alt="Vista previa del QR de pago"
                  className="size-32 rounded bg-white object-contain p-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={onPaymentQrRemove}>
                  Quitar QR
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="registration_declaration">Declaración legal</Label>
            <Textarea
              id="registration_declaration"
              rows={3}
              placeholder="Vacío = texto oficial de la Convención Nacional Ordinaria"
              value={formData.registration_declaration}
              onChange={(e) => onFieldChange('registration_declaration', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || (isEditMode && !hasChanges)}
        >
          {isSubmitting
            ? isEditMode
              ? 'Guardando...'
              : 'Creando...'
            : isEditMode
              ? 'Guardar Cambios'
              : 'Crear Evento'}
        </Button>
      </div>
    </form>
  );
}
